import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';

const HelpSupport = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // FAQ Categories
  const faqCategories = [
    { id: 'all', name: 'All Categories', icon: 'HelpCircle' },
    { id: 'getting-started', name: 'Getting Started', icon: 'Play' },
    { id: 'collections', name: 'Collection Management', icon: 'Package' },
    { id: 'market', name: 'Market Analysis', icon: 'TrendingUp' },
    { id: 'account', name: 'Account & Billing', icon: 'CreditCard' },
    { id: 'technical', name: 'Technical Support', icon: 'Settings' }
  ];

  // FAQ Data
  const faqData = [
    {
      id: 1,
      category: 'getting-started',
      question: 'How do I get started with Crazy Rare Club?',
      answer: 'Getting started is easy! First, create your account by registering with your email. Once verified, you can begin adding collectibles to your portfolio using our intuitive Add Item feature. Start by photographing your items and using our smart search to identify and value them automatically.'
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'What types of collectibles can I track?',
      answer: 'Crazy Rare Club supports a wide variety of collectibles including coins, stamps, trading cards, watches, vintage items, art, memorabilia, and much more. Our database contains millions of items with real-time market data and authentication features.'
    },
    {
      id: 3,
      category: 'collections',
      question: 'How do I add items to my collection?',
      answer: 'Navigate to the "Add Item" section from your dashboard. You can upload photos of your collectible, use our smart search feature to identify it, or manually enter details. Our AI-powered system will help estimate values and provide market insights.'
    },
    {
      id: 4,
      category: 'collections',
      question: 'Can I organize my collection by categories?',
      answer: 'Yes! You can organize your collection using custom categories, tags, and filters. Create collections for different types of items, set up custom categories, and use our advanced sorting options to keep everything organized.'
    },
    {
      id: 5,
      category: 'market',
      question: 'How accurate are the market valuations?',
      answer: 'Our valuations are based on real-time market data from multiple sources including auction houses, dealer networks, and market databases. Values are updated regularly and include historical trends, comparable sales, and market analysis.'
    },
    {
      id: 6,
      category: 'market',
      question: 'Can I track market trends for my items?',
      answer: 'Absolutely! Our Market Analysis section provides detailed trends, price history, market alerts, and comparative analysis. You can set up notifications for significant price changes and view detailed analytics for your entire portfolio.'
    },
    {
      id: 7,
      category: 'account',
      question: 'What are the different membership tiers?',
      answer: 'We offer Free, Premium, and Professional tiers. Free includes basic tracking for up to 50 items. Premium adds unlimited items, advanced analytics, and priority support. Professional includes all features plus API access and bulk import tools.'
    },
    {
      id: 8,
      category: 'account',
      question: 'How do I upgrade my membership?',
      answer: 'You can upgrade from your Profile Settings under the Membership section. Choose your preferred plan and payment method. Upgrades are instant and you\'ll immediately have access to all premium features.'
    },
    {
      id: 9,
      category: 'technical',
      question: 'Is my collection data secure?',
      answer: 'Yes, security is our top priority. We use enterprise-grade encryption, secure cloud storage, and regular security audits. Your data is backed up daily and you maintain full control over privacy settings and data sharing.'
    },
    {
      id: 10,
      category: 'technical',
      question: 'Can I export my collection data?',
      answer: 'Premium and Professional members can export their collection data in multiple formats including CSV, PDF reports, and JSON. This feature is available in your Profile Settings under Data Management.'
    }
  ];

  // Filter FAQs based on search and category
  const filteredFaqs = faqData?.filter(faq => {
    const matchesSearch = faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || 
                         faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq?.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // Quick Links
  const quickLinks = [
    {
      title: 'User Guide',
      description: 'Complete guide to using all platform features',
      icon: 'BookOpen',
      action: () => console.log('Open user guide')
    },
    {
      title: 'Video Tutorials',
      description: 'Step-by-step video walkthroughs',
      icon: 'Play',
      action: () => console.log('Open video tutorials')
    },
    {
      title: 'Community Forum',
      description: 'Connect with other collectors',
      icon: 'Users',
      action: () => console.log('Open community forum')
    },
    {
      title: 'Contact Support',
      description: 'Get help from our support team',
      icon: 'MessageCircle',
      action: () => console.log('Open contact form')
    }
  ];

  // Contact Options
  const contactOptions = [
    {
      method: 'Live Chat',
      description: 'Available Mon-Fri, 9 AM - 6 PM EST',
      icon: 'MessageCircle',
      action: () => console.log('Open live chat')
    },
    {
      method: 'Email Support',
      description: 'support@crazyrarecclub.com',
      icon: 'Mail',
      action: () => window?.location?.assign('mailto:support@crazyrarecclub.com')
    },
    {
      method: 'Submit Ticket',
      description: 'For detailed technical issues',
      icon: 'FileText',
      action: () => console.log('Open ticket form')
    }
  ];

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="p-6 lg:p-8 max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="HelpCircle" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Help & Support Center</h1>
                <p className="text-muted-foreground">
                  Find answers, get help, and connect with our support team
                </p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-2xl mx-auto">
              <Icon name="Search" size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search for help articles, tutorials, or FAQ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-4 py-4 bg-card border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-6">Quick Links</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickLinks?.map((link, index) => (
                <button
                  key={index}
                  onClick={link?.action}
                  className="bg-card border border-border rounded-xl p-6 premium-hover transition-all duration-300 text-left group"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-200">
                    <Icon name={link?.icon} size={20} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{link?.title}</h3>
                  <p className="text-sm text-muted-foreground">{link?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Frequently Asked Questions</h2>
              <span className="text-sm text-muted-foreground">
                {filteredFaqs?.length} article{filteredFaqs?.length !== 1 ? 's' : ''} found
              </span>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {faqCategories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setActiveCategory(category?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeCategory === category?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  <Icon name={category?.icon} size={16} />
                  <span>{category?.name}</span>
                </button>
              ))}
            </div>

            {/* FAQ List */}
            <div className="space-y-4">
              {filteredFaqs?.map((faq) => (
                <div key={faq?.id} className="bg-card border border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleFaq(faq?.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors duration-200"
                  >
                    <h3 className="font-medium text-foreground pr-4">{faq?.question}</h3>
                    <Icon 
                      name={expandedFaq === faq?.id ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      className="text-muted-foreground flex-shrink-0" 
                    />
                  </button>
                  {expandedFaq === faq?.id && (
                    <div className="px-6 pb-4 border-t border-border bg-muted/30">
                      <p className="text-muted-foreground leading-relaxed pt-4">{faq?.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredFaqs?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search terms or browse different categories
                </p>
              </div>
            )}
          </div>

          {/* Contact Support */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-6">Still Need Help?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contactOptions?.map((option, index) => (
                <button
                  key={index}
                  onClick={option?.action}
                  className="bg-card border border-border rounded-xl p-6 premium-hover transition-all duration-300 text-left group"
                >
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-success/20 transition-colors duration-200">
                    <Icon name={option?.icon} size={20} className="text-success" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{option?.method}</h3>
                  <p className="text-sm text-muted-foreground">{option?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Resources */}
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lightbulb" size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Can't Find What You're Looking For?</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Our support team is here to help! Contact us directly for personalized assistance 
                with your collectibles management and any technical questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 font-medium">
                  Contact Support Team
                </button>
                <button className="px-6 py-3 bg-transparent border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors duration-200 font-medium">
                  Browse Community Forum
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpSupport;